import * as React from "react";
import {
    GridToolbarContainer,
    useGridApiContext,
    gridColumnVisibilityModelSelector,
    useGridSelector,
    GridToolbarExportContainer,
    GridPrintExportMenuItem,
    GridCsvExportMenuItem,
    GridSelectionModel,
    GridRowsProp,
} from "@mui/x-data-grid";
import {
    Button,
    Popover,
    FormGroup,
    FormControlLabel,
    FormControl,
    OutlinedInput,
    InputAdornment,
    Typography,
    Box,
    SwitchProps,
    Switch,
    styled,
    MenuItem,
    Select,
    Chip,
    Menu,
    Checkbox,
} from "@mui/material";
import {
    Download,
    Columns3,
    FilterX,
    Filter,
    Search,
    X,
    SearchCheckIcon,
    Edit3,
    Hand,
} from "lucide-react";
import { theme } from "../../theme";
import { SoecoButton } from "../SoecoButton";
import { Company, Inventory, Product, ProductCategory } from "../../types";
import AddEmailDialog from "../AddEmailDialog";
import RegisterInterestProductDialog from "../RegisterInterestProductDialog";
import { HorizontalRule } from "@mui/icons-material";
type CustomColumnsButtonProps = {
    selectedRows: GridSelectionModel;
    setBulkEditOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSumOpen: React.Dispatch<React.SetStateAction<boolean>>;
    merged: boolean;
    filteredRows: GridRowsProp;
    products: Product[];
    inventories: Inventory[];
    companies: Company[];
};

type CustomCheckboxProps = {
    checked: boolean;
    row: any;
};
const CustomColumnsButton: React.FC<CustomColumnsButtonProps> = ({
    selectedRows,
    setBulkEditOpen,
    setSumOpen,
    merged,
    filteredRows,
    products,
}) => {
    const apiRefContext = useGridApiContext();
    const visibilityModel = useGridSelector(apiRefContext, gridColumnVisibilityModelSelector);
    const columns = apiRefContext.current
        .getAllColumns()
        .filter(col => col.field !== "__check__" && col.hideable !== false);

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [searchColumn, setSearchColumn] = React.useState("");

    const open = Boolean(anchorEl);

    React.useEffect(() => {
        localStorage.setItem("columns", JSON.stringify(visibilityModel));
    }, [visibilityModel]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const toggleColumn = (field: string) => {
        apiRefContext.current.setColumnVisibility(field, !visibilityModel[field]);
    };

    return (
        <>
            <Button
                variant="outlined"
                size="small"
                onClick={handleClick}
                startIcon={<Columns3 size={16} color={theme.palette.primary.main} />}
                sx={{ color: "#8c8c8c" }}
            >
                Columns
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                anchorReference="none"
                onClose={handleClose}
                anchorOrigin={{ vertical: "center", horizontal: "center" }}
                transformOrigin={{ vertical: "center", horizontal: "center" }}
                PaperProps={{
                    sx: {
                        p: 2,
                        borderRadius: "20px",
                        position: "absolute",
                    },
                }}
            >
                <Box
                    position="absolute"
                    top={8}
                    right={8}
                    onClick={handleClose}
                    sx={{ cursor: "pointer" }}
                >
                    <X />
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center" gap="8px">
                    <Typography color="#8c8c8c" align="center" sx={{ fontSize: "18px" }}>
                        Choose columns to view
                    </Typography>{" "}
                    <FormControl>
                        <OutlinedInput
                            id="outlined-adornment-amount"
                            placeholder="Find column"
                            value={searchColumn}
                            onChange={e => setSearchColumn(e.target.value)}
                            startAdornment={
                                <InputAdornment position="start">
                                    <Search size={20} color={theme.palette.primary.main} />
                                </InputAdornment>
                            }
                            sx={{
                                borderRadius: "50px",
                                height: "30px",
                            }}
                        />
                    </FormControl>
                </Box>
                <FormGroup sx={{ "& > *": { mt: 0.5, mb: 0.5 } }}>
                    {columns
                        .filter(col =>
                            col.headerName?.toLowerCase().includes(searchColumn.toLowerCase()),
                        )
                        .map(col => (
                            <FormControlLabel
                                key={col.field}
                                control={
                                    <IOSSwitch
                                        checked={visibilityModel[col.field] ?? true}
                                        onChange={() => toggleColumn(col.field)}
                                    />
                                }
                                label={col.headerName}
                                sx={{
                                    gap: 2,
                                    ml: 0.5,
                                    alignItems: "center",
                                }}
                            />
                        ))}
                </FormGroup>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "8px",
                        gap: "8px",
                    }}
                >
                    <SoecoButton
                        startIcon={<FilterX color={theme.palette.primary.main} />}
                        color={"warning"}
                        onClick={() =>
                            columns.forEach(col => {
                                if (visibilityModel[col.field]) {
                                    toggleColumn(col.field);
                                }
                            })
                        }
                    >
                        HIDE ALL
                    </SoecoButton>
                    <SoecoButton
                        startIcon={<Filter color="white" />}
                        color={"primary"}
                        onClick={() =>
                            columns.forEach(col => {
                                if (!visibilityModel[col.field]) {
                                    toggleColumn(col.field);
                                }
                            })
                        }
                    >
                        SHOW ALL
                    </SoecoButton>
                </div>
            </Popover>
            {!merged && (
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => setBulkEditOpen(true)}
                    disabled={!(selectedRows?.length > 0)}
                    sx={{
                        height: 30.35,
                        fontSize: "13px",
                        backgroundColor: `${theme.palette.secondary.main}`,
                        color: "#000",
                        "&:hover": {
                            backgroundColor: "#b18800ea",
                        },
                    }}
                    aria-label="Bulk edit "
                >
                    <Edit3 />
                </Button>
            )}

            <Button
                variant="contained"
                size="small"
                onClick={() => setSumOpen(true)}
                // disabled={!(selectedRows?.length > 0)}
                sx={{
                    height: 30.35,
                    fontSize: "13px",
                    backgroundColor: `${theme.palette.secondary.main}`,
                    color: "#000",
                    "&:hover": {
                        backgroundColor: "#b18800ea",
                    },
                }}
            >
                SUM
            </Button>
        </>
    );
};
const IOSSwitch = styled((props: SwitchProps) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 32,
    height: 18,
    padding: 1,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: 1,
        transitionDuration: "300ms",
        border: "1px solid black",

        "&.Mui-checked": {
            transform: "translateX(14px)",
            color: "#fff",
            "& + .MuiSwitch-track": {
                border: "1px solid black",
                backgroundColor: "#000",
                opacity: 1,
            },
        },
    },
    "& .MuiSwitch-thumb": {
        boxSizing: "border-box",
        width: 14,
        height: 14,
        border: "1px solid black",
        top: 20,
    },
    "& .MuiSwitch-track": {
        borderRadius: 26 / 2,
        backgroundColor: "white",
        border: "2px solid black",
        opacity: 1,
    },
}));

type SearchChip = { field: string; value: string };
type Props = {
    handleCreateReport: (columns: string[]) => void;
    chips: SearchChip[];
    setChips: React.Dispatch<React.SetStateAction<SearchChip[]>>;
    productCategories: ProductCategory[];
    selectedRows: GridSelectionModel;
    setBulkEditOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSumOpen: React.Dispatch<React.SetStateAction<boolean>>;
    merged: boolean;
    filteredRows: GridRowsProp;
    allProducts: Product[];
    inventories: Inventory[];
    companies: Company[];
};

export const CustomToolbar: React.FC<Props> = ({
    handleCreateReport,
    chips,
    setChips,
    selectedRows,
    setBulkEditOpen,
    setSumOpen,
    productCategories,
    merged,
    filteredRows,
    allProducts,
    inventories,
    companies,
}) => {
    const apiRefContext = useGridApiContext();
    const visibilityModel = useGridSelector(apiRefContext, gridColumnVisibilityModelSelector);
    const [products, setProducts] = React.useState<Product[]>([]);
    const [exportMenuOpen, setExportMenuOpen] = React.useState(false);
    const exportMenuTriggerRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        const newProducts = selectedRows.reduce((prev, row) => {
            const product = allProducts.find(p => p.id === row);
            if (product) {
                return [...prev, product];
            }
            return prev;
        }, [] as Product[]);
        setProducts(newProducts);
    }, [selectedRows]);

    React.useEffect(() => {
        const storedChips = localStorage.getItem("chips");
        if (storedChips) {
            try {
                const parsed = JSON.parse(storedChips) as SearchChip[];
                if (Array.isArray(parsed)) {
                    setChips(parsed);
                }
            } catch (e) {
                console.error("Failed to parse stored chips", e);
            }
        }
    }, []);

    React.useEffect(() => {
        localStorage.setItem("chips", JSON.stringify(chips));
    }, [chips]);

    const [searchField, setSearchField] = React.useState<SearchChip["field"]>("name");
    const [searchText, setSearchText] = React.useState("");
    const [registerInterestProductDialogOpen, setRegisterInterestProductDialogOpen] =
        React.useState(false);

    const handleCloseRegisterInterestProductDialog = () => {
        setRegisterInterestProductDialogOpen(false);
    };
    const handleAddChip = () => {
        if (!searchText.trim()) return;
        setChips(prev => {
            const value = searchText;

            if (searchField === "categories") {
                const matches = productCategories.filter(c =>
                    c.name.toLowerCase().includes(searchText.toLowerCase()),
                );

                if (matches.length > 0) {
                    const newChips = matches.map(m => ({ field: "categories", value: m.id }));

                    const filtered = prev.filter(chip => chip.field !== "categories");
                    return [...filtered, ...newChips];
                } else {
                    return prev;
                }
            }

            const filtered = prev.filter(chip => chip.field !== searchField);
            return [...filtered, { field: searchField, value }];
        });

        setSearchText("");
    };
    const handleDeleteChip = (index: number) => {
        setChips(prev => prev.filter((_, i) => i !== index));
    };

    const placeholderMap: Record<SearchChip["field"], string> = {
        name: "Name",
        roomPlacement: "Room",
        warehousePlacement: "Building",
        categories: "Categories",
    };
    return (
        <GridToolbarContainer style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                    ref={exportMenuTriggerRef}
                    id="export-menu-trigger"
                    aria-controls="export-menu"
                    aria-haspopup="true"
                    aria-expanded={exportMenuOpen ? "true" : undefined}
                    onClick={() => setExportMenuOpen(true)}
                    startIcon={<Download size={16} color={theme.palette.primary.main} />}
                    variant="outlined"
                    size="small"
                    sx={{ color: "#8c8c8c" }}
                >
                    EXPORT
                </Button>
                <Menu
                    id="export-menu"
                    anchorEl={exportMenuTriggerRef.current}
                    open={exportMenuOpen}
                    onClose={() => setExportMenuOpen(false)}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                >
                    <GridCsvExportMenuItem options={{ delimiter: "," }} />
                    <GridPrintExportMenuItem />
                    <MenuItem
                        onClick={() =>
                            handleCreateReport(
                                Object.keys(visibilityModel).filter(key => visibilityModel[key]),
                            )
                        }
                    >
                        Excel
                    </MenuItem>
                </Menu>

                <CustomColumnsButton
                    selectedRows={selectedRows}
                    setBulkEditOpen={setBulkEditOpen}
                    setSumOpen={setSumOpen}
                    merged={merged}
                    filteredRows={filteredRows}
                    products={products}
                    inventories={inventories}
                    companies={companies}
                />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FormControl size="small" sx={{ height: 30.35 }}>
                    <Select
                        value={searchField}
                        onChange={e => {
                            const value = e.target.value;
                            if (
                                value === "name" ||
                                value === "warehousePlacement" ||
                                value === "roomPlacement" ||
                                value === "categories"
                            ) {
                                setSearchField(value);
                            }
                        }}
                        sx={{ height: 30.35, fontSize: "13px", color: "#8c8c8c" }}
                    >
                        <MenuItem value="name" sx={{ fontSize: "13px" }}>
                            Name
                        </MenuItem>
                        <MenuItem value="warehousePlacement" sx={{ fontSize: "13px" }}>
                            Building
                        </MenuItem>
                        <MenuItem value="roomPlacement" sx={{ fontSize: "13px" }}>
                            Room
                        </MenuItem>
                        <MenuItem value="categories" sx={{ fontSize: "13px" }}>
                            Categories
                        </MenuItem>
                    </Select>
                </FormControl>

                <OutlinedInput
                    placeholder={`Search ${placeholderMap[searchField]}`}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddChip();
                        }
                    }}
                    sx={{ width: "50%", height: 30.35, padding: "3px 9px", fontSize: "13px" }}
                />
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddChip}
                    sx={buttonSx}
                    aria-label="Search "
                >
                    <Search size={18} />
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => setRegisterInterestProductDialogOpen(true)}
                    sx={buttonSx}
                    disabled={!(selectedRows?.length > 0)}
                >
                    <Hand size={18} />
                </Button>
                <RegisterInterestProductDialog
                    isAddEmailDialogOpen={registerInterestProductDialogOpen}
                    handleCloseAddEmailDialog={handleCloseRegisterInterestProductDialog}
                    setNewEmail={setSearchText}
                    products={products}
                    companies={companies}
                    inventories={inventories}
                />
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {chips.map((chip: SearchChip, index) => (
                    <Chip
                        key={index}
                        label={
                            chip.field === "categories"
                                ? `Category: ${
                                      productCategories.find(c => c.id === chip.value)?.name ??
                                      chip.value
                                  }`
                                : `${placeholderMap[chip.field]}: ${chip.value}`
                        }
                        onDelete={() => handleDeleteChip(index)}
                        size="small"
                    />
                ))}
            </Box>
        </GridToolbarContainer>
    );
};
const buttonSx = {
    height: 30.35,
    fontSize: "13px",
    backgroundColor: `${theme.palette.secondary.main}`,
    color: "#000",
    "&:hover": {
        backgroundColor: "#b18800ea",
    },
};
